import React from 'react';
import { ConnectedRetailVirtualSlot } from '../../lib/types';
import { formatCurrency } from '../../lib/export';
import { Package, Home, Store, Truck } from 'lucide-react';

interface ConnectedRetailVirtualSlotsTableProps {
  data: ConnectedRetailVirtualSlot[];
  className?: string;
}

export function ConnectedRetailVirtualSlotsTable({ data, className = '' }: ConnectedRetailVirtualSlotsTableProps) {
  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'home_delivery':
        return <Home className="h-4 w-4" />;
      case 'store_pickup':
        return <Store className="h-4 w-4" />;
      case 'direct_ship':
        return <Truck className="h-4 w-4" />;
      default:
        return <Package className="h-4 w-4" />;
    }
  };

  const getSourceLabel = (source: string) => {
    return source.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const totalRevenue = data.reduce((sum, slot) => sum + slot.revenue, 0);
  const totalSales = data.reduce((sum, slot) => sum + slot.totalSales, 0);

  return (
    <div className={`bg-white p-6 rounded-lg shadow ${className}`}>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Connected Retail Virtual Slots</h3>
        <p className="text-sm text-gray-600">Virtual product slots extending machine inventory beyond physical capacity</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-blue-600 mb-1">Total Virtual Slots</p>
          <p className="text-2xl font-bold text-blue-900">{data.length}</p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <p className="text-sm text-green-600 mb-1">Total Sales</p>
          <p className="text-2xl font-bold text-green-900">{totalSales}</p>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <p className="text-sm text-purple-600 mb-1">Total Revenue</p>
          <p className="text-2xl font-bold text-purple-900">{formatCurrency(totalRevenue)}</p>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 font-medium text-gray-900">Slot Name</th>
              <th className="text-left py-3 px-4 font-medium text-gray-900">Machine</th>
              <th className="text-left py-3 px-4 font-medium text-gray-900">Product</th>
              <th className="text-left py-3 px-4 font-medium text-gray-900">Source</th>
              <th className="text-right py-3 px-4 font-medium text-gray-900">Virtual Stock</th>
              <th className="text-right py-3 px-4 font-medium text-gray-900">Sales</th>
              <th className="text-right py-3 px-4 font-medium text-gray-900">Revenue</th>
            </tr>
          </thead>
          <tbody>
            {data.map((slot) => (
              <tr key={slot.slotId} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-3 px-4 text-sm font-medium text-gray-900">{slot.slotName}</td>
                <td className="py-3 px-4 text-sm text-gray-600">{slot.machineName}</td>
                <td className="py-3 px-4 text-sm text-gray-600">{slot.productName}</td>
                <td className="py-3 px-4 text-sm">
                  <div className="flex items-center gap-2">
                    {getSourceIcon(slot.source)}
                    <span className="text-gray-600">{getSourceLabel(slot.source)}</span>
                  </div>
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-900">{slot.virtualStock}</td>
                <td className="py-3 px-4 text-sm text-right text-gray-900">{slot.totalSales}</td>
                <td className="py-3 px-4 text-sm text-right font-medium text-green-600">
                  {formatCurrency(slot.revenue)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

